import type { BuildingDef, BuildingType } from './types';

export const MAP_W = 64;
export const MAP_H = 64;
export const COLS = MAP_W * MAP_H;
export const MAX_DEPTH = 6;

export const colOf = (x: number, y: number): number => y * MAP_W + x;
export const xOf = (col: number): number => col % MAP_W;
export const yOf = (col: number): number => Math.floor(col / MAP_W);
export const cellOf = (col: number, z: number): number => z * COLS + col;
export const inBounds = (x: number, y: number): boolean => x >= 0 && x < MAP_W && y >= 0 && y < MAP_H;

export const TICK_S = 0.1; // 10 Hz sim
export const MAX_STEPS_PER_FRAME = 40;
export const TICKS_PER_DAY = 200; // 20 s/day at 1x
export const DAYS_PER_WEEK = 7;
export const TICKS_PER_WEEK = TICKS_PER_DAY * DAYS_PER_WEEK;

export const JOB_ASSIGN_INTERVAL_TICKS = 5;
export const PATH_BUDGET_PER_TICK = 40;

/** Where the company bus/truck enters: middle of the west edge. */
export const SPAWN_EDGE = { x: 0, y: 32 };
/** Survey stake / HQ area — terraingen guarantees iron near here. */
export const STAKE = { x: 20, y: 32 };

export const BALANCE = {
  startCashCents: 120_000,
  startWorkers: 6,
  rentCentsPerWeek: 800,
  foodSpendCentsPerWeek: 600,
  saloonSpendCentsPerWeek: 500,
  saloonUptake: 0.7,
  casinoSpendCentsPerWeek: 900,
  casinoUptake: 0.5,

  ironPriceMeanCents: 4_200, // $/ton in cents — boom years should bank a war chest
  ironPriceClamp: [2_500, 6_000] as const,
  ironThetaPerWeek: 0.25,
  ironSigmaPerWeek: 250,
  goldPriceMeanCents: 30_000,
  goldPriceClamp: [14_000, 60_000] as const,
  goldThetaPerWeek: 0.1,
  goldSigmaPerWeek: 3_500,
  goldSpikeChancePerWeek: 0.05,
  goldSpikeMultiplier: 1.5,
  goldSpikeWeeks: 2,

  digTicksBase: 60, // 6 s at surface at 1x
  digTicksDepthFactor: 0.25,
  haulerCarryDt: 10,
  depotCapacityDt: 600,
  truckAutoDepartDt: 200,
  truckMinManualDt: 50,
  truckFeeCents: 4_000,
  truckSettleDays: 1,

  peepSpeedTilesPerSec: 3.0,
  slopeSpeedFactor: 0.75,
  roadSpeedFactor: 1.6,
  carrySpeedFactor: 0.85,
  slopePathCost: 1.5,
  roadPathCost: 0.625,
  // Cliffs (>1 level) are climbable but punishing, so pits can never trap
  // peeps or strand ore — ramps stay the efficient choice, not the only one.
  climbPathCostPerLevel: 4,
  climbSpeedFactor: 0.25,

  waterUnitsPerWorkerPerDay: 1,
  waterRefillCents: 12_000,
  waterRefillUnits: 400,
  waterRefillThreshold: 0.3,

  drinkUnlockPop: 10,
  gamblingUnlockPop: 22,
  needWeights: { housing: 0.3, food: 0.25, water: 0.15, drink: 0.15, gambling: 0.05, paidOnTime: 0.1 },
  moraleSmoothing: 0.2,
  missedPayrollMoraleHit: 0.25,

  quitBase: 0.6,
  quitScale: 0.25,
  quitMissedPayrollFactor: 0.15,

  incidentBase: 0.01,
  incidentSaloon: 0.02,
  incidentCasino: 0.05,
  incidentSheriffFactor: 0.35,
  incidentMinGapDays: 2,
  theftMaxCents: 30_000,
  theftCashFraction: 0.1,

  bailoutCents: 150_000,
  debtInterestPerWeek: 0.05,

  // ---- Stage 3: tiers & families ----
  /** Weeks of counted tenure to reach Regular / Veteran. Settler = family. */
  tierTenureWeeks: [0, 2, 6] as const,
  /** A week only counts toward tenure when morale is at least this. */
  tierMoraleGate: 0.55,
  /** Weekly wage by tier (cents) — miners and haulers only; building staff
   * are unpaid (their building's takings cover them). */
  tierWageCents: [5_000, 5_500, 6_200, 7_200] as const,
  /** Work-speed multiplier by tier (digging and hauling). */
  tierSpeed: [1, 1.08, 1.16, 1.25] as const,
  /** Quit-probability multiplier by tier. */
  tierQuitFactor: [1, 0.7, 0.4, 0.1] as const,
  /** Named requests before settlement goes automatic. */
  familyLetterCount: 3,
  familyRequestExpiryDays: 14,
  familyDeclineMoraleHit: 0.05,
  familyReaskWeeks: 6,
  /** Auto settlements/week scale with town size: 1 + floor(households/8), capped. */
  familyAutoPerHouseholds: 8,
  familyAutoMax: 6,
  kidsPerFamilyMax: 2,
  /** Kids come of age after settleWeek + this many weeks (+0..8 jitter). */
  kidGrowUpWeeks: 20,
  /** Household rent by home type is on the def; family store spend: */
  familyStoreSpendCents: 800,
  /** Dust: excavated/ordered tiles within this Chebyshev radius of a home
   * raise its dust level; at dustTilesForMax the air need bottoms out. */
  dustRadius: 6,
  dustTilesForMax: 14,
  /** Weight of family needs in overall morale once households exist. */
  familyMoraleWeight: 0.3,

  // ---- Stage 4: incorporation, town economy, endgame ----
  charterHouseholds: 3,
  charterPop: 25,
  /** Weekly town tax per household, scaled by household happiness. */
  townTaxCentsPerWeek: 500,
  /** Weekly commerce income per resident, scaled by attractiveness. */
  commerceCentsPerPersonPerWeek: 60,
  railIncomeBaseCents: 20_000,
  /** Ore sells 10% higher while a functional rail depot exists. */
  railOreBonus: 0.1,
  bankInterestPerWeek: 0.005,
  bankInterestCapCents: 5_000,
  /** Pull-out triggers when past this week AND the mine is fading. */
  pulloutMinWeek: 25,
  pulloutOreValueThresholdCents: 150_000,
  pulloutIncomeFraction: 0.3,
  pulloutNagWeeks: 2,
  /** Strip & Leave settlement: salvage per building + a cut of remaining ore.
   * Ore components are capped — HQ writes off deep unproven reserves — so an
   * ore-rich map neither makes the buyout unpayable nor strip a jackpot. */
  stripPerBuildingCents: 10_000,
  stripOreFraction: 0.25,
  stripOreCapCents: 200_000,
  /** Buyout: HQ prices the town, spread over installments with interest.
   * Scale: a strong company era banks ~$2.5-4k, so a full-price buyout lands
   * near $3.5k — payable from town income plus a war chest, never trivially. */
  buyoutBaseCents: 150_000,
  buyoutPerBuildingCents: 5_000,
  buyoutOreFraction: 0.5,
  buyoutOreCapCents: 150_000,
  buyoutBankDiscount: 0.2,
  buyoutWeeks: 10,
  buyoutInterest: 0.15,
  buyoutMissPenalty: 0.02,
  /** The Town Endures test. */
  endureWeeksNeeded: 12,
  endurePopMin: 25,
  endureMoraleMin: 0.5,
  endureMineShareMax: 0.2,
  /** Ghost town collapse. */
  ghostPopMin: 8,
  ghostInsolventWeeks: 6,
  /** Post-incorporation resident immigration: attempts/week grow with vacancy. */
  immigrationAttractMin: 0.4,
  immigrationVacancyDivisor: 5,
  immigrationAttemptsMax: 10,
  /** Weekly hiring-bus applicants cap (base + morale + saloon + vacancy). */
  applicantsMax: 12,
  pavedSpeedFactor: 2.0,

  quotaIronDt: 1_500,
  quotaByWeek: 8,
  quotaRewardCents: 50_000,

  // ---- Stage 5: post-ore industry ----
  /** Per-good price walks (cents/unit), same OU shape as iron/gold. Drift
   * order is iron, gold, then these in GOOD_KINDS order, then power — the
   * market fork's draw sequence depends on it. Tuned so a single farm barely
   * clears its farmhand's wage (tight), while the full corn→cattle campus
   * plus freight nets ~$250-300/wk — enough to carry a buyout. */
  commodityMarket: {
    corn: { meanCents: 250, clamp: [120, 450], thetaPerWeek: 0.3, sigmaPerWeek: 25 },
    wheat: { meanCents: 450, clamp: [220, 780], thetaPerWeek: 0.3, sigmaPerWeek: 45 },
    cattle: { meanCents: 2_100, clamp: [1_000, 3_600], thetaPerWeek: 0.2, sigmaPerWeek: 170 },
    glass: { meanCents: 900, clamp: [500, 1_600], thetaPerWeek: 0.2, sigmaPerWeek: 80 },
    chips: { meanCents: 6_000, clamp: [3_000, 12_000], thetaPerWeek: 0.12, sigmaPerWeek: 600 },
    /** Sold weekly per unit of interconnected surplus supply, not trucked. */
    power: { meanCents: 700, clamp: [300, 1_500], thetaPerWeek: 0.2, sigmaPerWeek: 80 },
  },
  /** Producers hard-stop when their output pile is full — no free storage. */
  goodsPileCapUnits: 60,
  goodsHaulerCarryUnits: 10,
  /** Goods freight is compact: cheaper flat fee than the ore truck. */
  goodsTruckFeeCents: 3_000,
  goodsTruckAutoDepartValueCents: 25_000,
  goodsTruckSettleDays: 1,
  /** Same rail lever as ore — the depot serves both docks. */
  goodsRailBonus: 0.1,
  /** Market Hall: residents buy this many stored units each, at a premium. */
  retailUnitsPerPersonPerWeek: 1,
  retailPremium: 1.15,
  /** Boom/bust shocks (generalizes the old gold spike): one weekly roll, any
   * commodity, announced by letter/toast, mean-reversion cleans up after. */
  marketEventChancePerWeek: 0.06,
  marketEventBoomMultiplier: 1.6,
  marketEventBustMultiplier: 0.55,
  marketEventWeeks: 2,
  /** Nuclear scram: rare unplanned outage, days of dark, then back online. */
  scramChancePerWeek: 0.02,
  scramOutageDaysMin: 2,
  scramOutageDaysMax: 4,
  /** Passenger rail: weekly fares × (0.5 + attract*1.5) × min(1, pop/50). */
  passengerBaseCents: 8_000,

  ledgerCap: 200,
  incidentLogCap: 100,
} as const;

export const BUILDING_DEFS: Record<BuildingType, BuildingDef> = {
  hq: { label: 'Company HQ', costCents: 0, w: 2, h: 2, staffRequired: 0, powerDraw: 0 },
  trailer: { label: 'Trailer', costCents: 15_000, w: 1, h: 1, beds: 2, staffRequired: 0, powerDraw: 0 },
  bunkhouse: { label: 'Bunkhouse', costCents: 50_000, w: 2, h: 2, beds: 12, staffRequired: 0, powerDraw: 1 },
  cottage: {
    label: 'Cottage',
    costCents: 35_000,
    w: 1,
    h: 1,
    familyHomes: 1,
    homeComfort: 0,
    rentCentsPerWeek: 1_500,
    staffRequired: 0,
    powerDraw: 1,
    waterPerDay: 1,
  },
  familyHouse: {
    label: 'Family House',
    costCents: 90_000,
    w: 2,
    h: 2,
    familyHomes: 1,
    homeComfort: 0.15,
    rentCentsPerWeek: 2_500,
    staffRequired: 0,
    powerDraw: 1,
    waterPerDay: 2,
  },
  townhome: {
    label: 'Townhomes',
    costCents: 70_000,
    w: 2,
    h: 2,
    familyHomes: 3,
    homeComfort: 0.05,
    rentCentsPerWeek: 1_200,
    staffRequired: 0,
    powerDraw: 2,
    waterPerDay: 3,
    unlockedBy: 'charter',
  },
  apartment: {
    label: 'Apartments',
    costCents: 200_000,
    w: 3,
    h: 3,
    familyHomes: 6,
    homeComfort: -0.05,
    rentCentsPerWeek: 1_000,
    staffRequired: 0,
    powerDraw: 3,
    waterPerDay: 6,
    unlockedBy: 'charter',
  },
  canteen: {
    label: 'Canteen',
    costCents: 40_000,
    w: 2,
    h: 2,
    foodCap: 40,
    staffRequired: 1,
    powerDraw: 1,
    waterPerDay: 3,
  },
  generalStore: { label: 'General Store', costCents: 45_000, w: 2, h: 2, foodCap: 60, staffRequired: 1, powerDraw: 1 },
  saloon: {
    label: 'Saloon',
    costCents: 60_000,
    w: 2,
    h: 2,
    drinkCap: 50,
    staffRequired: 1,
    powerDraw: 1,
    waterPerDay: 4,
  },
  casino: {
    label: 'Casino',
    costCents: 150_000,
    w: 3,
    h: 3,
    gambleCap: 100,
    staffRequired: 2,
    powerDraw: 3,
    waterPerDay: 5,
    unlockedBy: 'casino-unlocked',
    lockHint: 'Locked — meet the HQ quota first',
  },
  sheriff: { label: "Sheriff's Office", costCents: 80_000, w: 2, h: 2, staffRequired: 1, powerDraw: 1 },
  school: {
    label: 'School',
    costCents: 70_000,
    w: 2,
    h: 2,
    service: 'education',
    serviceCap: 100,
    staffRequired: 1,
    powerDraw: 1,
    waterPerDay: 1,
  },
  hospital: {
    label: 'Hospital',
    costCents: 110_000,
    w: 2,
    h: 2,
    service: 'health',
    serviceCap: 500,
    staffRequired: 1,
    powerDraw: 2,
    waterPerDay: 3,
  },
  communityHall: {
    label: 'Community Hall',
    costCents: 60_000,
    w: 2,
    h: 2,
    service: 'community',
    serviceCap: 300,
    staffRequired: 1,
    powerDraw: 1,
  },
  chapel: {
    label: 'Chapel',
    costCents: 45_000,
    w: 2,
    h: 2,
    service: 'community',
    serviceCap: 150,
    staffRequired: 1,
    powerDraw: 0,
    attract: 0.05,
  },
  townHall: {
    label: 'Town Hall',
    costCents: 120_000,
    w: 2,
    h: 2,
    staffRequired: 1,
    powerDraw: 1,
    attract: 0.15,
    unlockedBy: 'charter',
  },
  bank: {
    label: 'Bank',
    costCents: 90_000,
    w: 2,
    h: 2,
    staffRequired: 1,
    powerDraw: 1,
    attract: 0.1,
    unlockedBy: 'charter',
  },
  park: {
    label: 'Park',
    costCents: 25_000,
    w: 2,
    h: 2,
    service: 'community',
    serviceCap: 200,
    staffRequired: 0,
    powerDraw: 0,
    waterPerDay: 1,
    attract: 0.15,
    unlockedBy: 'charter',
  },
  publicPool: {
    label: 'Public Pool',
    costCents: 150_000,
    w: 3,
    h: 3,
    service: 'community',
    serviceCap: 100,
    staffRequired: 1,
    powerDraw: 1,
    waterPerDay: 10,
    attract: 0.25,
    unlockedBy: 'charter',
  },
  university: {
    label: 'University',
    costCents: 300_000,
    w: 3,
    h: 3,
    service: 'education',
    serviceCap: 1000,
    staffRequired: 2,
    powerDraw: 2,
    waterPerDay: 2,
    attract: 0.25,
    unlockedBy: 'charter',
  },
  railDepot: {
    label: 'Rail Depot',
    costCents: 250_000,
    w: 3,
    h: 3,
    staffRequired: 1,
    powerDraw: 1,
    attract: 0.15,
    corridor: 'west',
    unlockedBy: 'charter',
  },
  oreDepot: { label: 'Ore Depot', costCents: 30_000, w: 2, h: 2, staffRequired: 0, powerDraw: 0 },
  generator: {
    label: 'Diesel Generator',
    costCents: 50_000,
    w: 2,
    h: 2,
    staffRequired: 0,
    powerDraw: 0,
    powerSupply: 10,
    fuelCentsPerWeek: 5_000,
  },
  powerPole: { label: 'Power Pole', costCents: 2_500, w: 1, h: 1, staffRequired: 0, powerDraw: 0 },
  waterTank: {
    label: 'Water Tank',
    costCents: 20_000,
    w: 1,
    h: 1,
    staffRequired: 0,
    powerDraw: 0,
    waterCapacity: 1_500,
  },
  well: {
    label: 'Aquifer Well',
    costCents: 120_000,
    w: 1,
    h: 1,
    staffRequired: 0,
    powerDraw: 2,
    wellYieldPerDay: 200,
  },
  // ---- Stage 5: post-ore industry. Staff here are waged like miners —
  // a town that farms pays farmhands, and the Endures ledger feels it. ----
  cornFarm: {
    label: 'Corn Farm',
    costCents: 45_000,
    w: 3,
    h: 3,
    staffRequired: 1,
    staffWaged: true,
    powerDraw: 0,
    waterPerDay: 4,
    produces: { good: 'corn', unitsPerDay: 10 },
    unlockedBy: 'charter',
  },
  wheatFarm: {
    label: 'Wheat Farm',
    costCents: 60_000,
    w: 3,
    h: 3,
    staffRequired: 1,
    staffWaged: true,
    powerDraw: 1,
    waterPerDay: 10,
    produces: { good: 'wheat', unitsPerDay: 8 },
    unlockedBy: 'charter',
  },
  cattleRanch: {
    label: 'Cattle Ranch',
    costCents: 90_000,
    w: 3,
    h: 3,
    staffRequired: 2,
    staffWaged: true,
    powerDraw: 1,
    waterPerDay: 14,
    produces: { good: 'cattle', unitsPerDay: 3 },
    consumes: { good: 'corn', unitsPerDay: 5, bufferUnits: 15 },
    unlockedBy: 'charter',
  },
  warehouse: {
    label: 'Warehouse',
    costCents: 40_000,
    w: 2,
    h: 2,
    staffRequired: 0,
    powerDraw: 0,
    goodsCapacityUnits: 300,
    unlockedBy: 'charter',
  },
  freightOffice: {
    label: 'Freight Office',
    costCents: 50_000,
    w: 2,
    h: 2,
    staffRequired: 1,
    staffWaged: true,
    powerDraw: 1,
    unlockedBy: 'charter',
  },
  goodsMarket: {
    label: 'Market Hall',
    costCents: 55_000,
    w: 2,
    h: 2,
    staffRequired: 1,
    staffWaged: true,
    powerDraw: 1,
    waterPerDay: 1,
    retail: true,
    attract: 0.05,
    unlockedBy: 'charter',
  },
  // ---- Stage 5 high tier: the university opens the modern age. ----
  glassworks: {
    label: 'Glassworks',
    costCents: 120_000,
    w: 3,
    h: 3,
    staffRequired: 3,
    staffWaged: true,
    powerDraw: 4,
    waterPerDay: 6,
    produces: { good: 'glass', unitsPerDay: 4 },
    unlockedBy: 'charter',
  },
  chipFab: {
    label: 'Semiconductor Fab',
    costCents: 250_000,
    w: 3,
    h: 3,
    staffRequired: 4,
    staffWaged: true,
    powerDraw: 8,
    waterPerDay: 12,
    produces: { good: 'chips', unitsPerDay: 1 },
    consumes: { good: 'glass', unitsPerDay: 2, bufferUnits: 8 },
    unlockedBy: 'university-modernity',
    lockHint: 'Locked — a working university must publish first',
  },
  solarArray: {
    label: 'Solar Array',
    costCents: 80_000,
    w: 3,
    h: 3,
    staffRequired: 0,
    powerDraw: 0,
    powerSupply: 6,
    unlockedBy: 'university-modernity',
    lockHint: 'Locked — a working university must publish first',
  },
  nuclearPlant: {
    label: 'Nuclear Plant',
    costCents: 400_000,
    w: 3,
    h: 3,
    staffRequired: 3,
    staffWaged: true,
    powerDraw: 0,
    powerSupply: 40,
    waterPerDay: 30,
    fuelCentsPerWeek: 2_000,
    unlockedBy: 'university-modernity',
    lockHint: 'Locked — a working university must publish first',
  },
  gridInterconnect: {
    label: 'Grid Interconnect',
    costCents: 150_000,
    w: 2,
    h: 2,
    staffRequired: 1,
    staffWaged: true,
    powerDraw: 0,
    corridor: 'east',
    unlockedBy: 'charter',
  },
  passengerTerminal: {
    label: 'Passenger Terminal',
    costCents: 90_000,
    w: 2,
    h: 2,
    staffRequired: 1,
    powerDraw: 1,
    attract: 0.1,
    unlockedBy: 'charter',
    lockHint: 'Locked — requires the town charter',
  },
};

export const POWER_LINK_RANGE = 4; // Chebyshev tiles between node centers
export const WATER_ADJACENT_RANGE = 3; // buildings this close to a tank count as connected pre-pipes

export const ROAD_COST_CENTS = 500;
export const PIPE_COST_CENTS = 1_500;
/** Buried power line per tile — dearer than stringing poles, but invisible. */
export const CABLE_COST_CENTS = 3_500;
/** Stone paving upgrade per road tile (incorporation-gated). */
export const PAVED_COST_CENTS = 1_500;
/** Filling a pit back to grade, per buried block — a 100-col, avg-2.5-deep
 * scar runs ≈ $2,000: healing the land is an investment, not a click. */
export const RECLAIM_COST_CENTS_PER_LEVEL = 800;
/** Half the build cost back when a route tile (road/pipe/cable/paving) is torn out. */
export const ROUTE_SALVAGE_FRACTION = 0.5;
